import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";

import PrivateRoutes from "../components/PrivateRoutes";
import RoleBasedRedirect from "../components/RoleBasedRedirect";
import { USER_TYPE } from "../constants/common";
import useAuth from "../hooks/useAuth";
import AuthLayout from "../layouts/AuthLayout";
import KidLayout from "../layouts/KidLayout";
import ParentLayout from "../layouts/ParentLayout";
import SchoolLayout from "../layouts/SchoolLayout";
import StudentLayout from "../layouts/StudentLayout";
import SuperAdminLayout from "../layouts/SuperAdminLayout";
import TeacherLayout from "../layouts/TeacherLayout";
import { getLanguage } from "../utils/getLanguage";
import { AUTHENTICATED_ROUTES, UNAUTHENTICATED_ROUTES } from "./paths";

const RootRouter = () => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuth();
  const lang = getLanguage();

  useEffect(() => {
    const pathParts = location.pathname.split("/");
    const newLang = pathParts[1] || i18n.language;

    if (newLang !== i18n.language) {
      i18n.changeLanguage(newLang);
      localStorage.setItem("lang", newLang);
    }

    if (!pathParts[1]) {
      navigate(`/${newLang}${location.pathname}`);
    }

    document.body.classList.toggle("rtl", newLang === "ar");
  }, [location, i18n, navigate]);

  return (
    <Routes>
      <Route path="/" element={<Navigate to={`/${lang}`} replace />} />

      <Route element={<AuthLayout />}>
        {UNAUTHENTICATED_ROUTES.map(({ path, element }) => (
          <Route
            path="/:lang"
            key={path}
            element={<RoleBasedRedirect element={element} role={user.role} lang={lang} />}
          >
            <Route path={path} element={<element />} />
          </Route>
        ))}
      </Route>

      {/* Conditional layouts based on user role */}
      {AUTHENTICATED_ROUTES.map(({ path, element: Element, roles }) => (
        <Route key={path} path="/:lang" element={<PrivateRoutes allowedRoles={roles} />}>
          <Route
            path={path}
            element={
              {
                [USER_TYPE.STUDENT.ROLE]: (
                  <StudentLayout>
                    <Element />
                  </StudentLayout>
                ),
                [USER_TYPE.TEACHER.ROLE]: (
                  <TeacherLayout>
                    <Element />
                  </TeacherLayout>
                ),
                [USER_TYPE.SCHOOL.ROLE]: (
                  <SchoolLayout>
                    <Element />
                  </SchoolLayout>
                ),
                [USER_TYPE.SUPERADMIN.ROLE]: (
                  <SuperAdminLayout>
                    <Element />
                  </SuperAdminLayout>
                ),
                [USER_TYPE.PARENT.ROLE]: (
                  <ParentLayout>
                    <Element />
                  </ParentLayout>
                ),
                [USER_TYPE.KID.ROLE]: (
                  <KidLayout>
                    <Element />
                  </KidLayout>
                ),
              }[user.role] || <Navigate to={`/${lang}`} replace /> // Redirect if no matching layout
            }
          />
        </Route>
      ))}

      <Route path="*" element={<Navigate to={`/${lang}`} replace />} />
    </Routes>
  );
};

export default RootRouter;
